import React, { useCallback } from "react";
import s from "../../common.scss";
import withStyles from "isomorphic-style-loader/withStyles";
import WizardFormStep from "../../components/WizardFormStep";
import FormFieldRow from "../components/FormFieldRow";
import FormInput, {
  SelectOption,
} from "../../../../../../toolkit/formInput";
import BaseSelect from "../../../../../../toolkit/baseSelectV2";
import { State, City } from "country-state-city";
import AsyncSelect from "../../../../../../toolkit/baseAsyncSelect";
import * as yup from "yup";
import {
  createOptionalTextField,
  createRequiredTextField,
  createSelectField,
} from "../../../../../../utils/validation/validationUtils";

export const businessAddressFormSchema = yup.object({
  addressLine1: createRequiredTextField("Address"),
  addressLine2: createOptionalTextField(),
  addressCity: createSelectField("Please select a city"),
  addressStateProvince: createSelectField("Please select a state/province"),
  addressPostalCode: createRequiredTextField("Postal code"),
  country: createSelectField("Please select a country"),
});

const countryOptions: SelectOption[] = [
  { value: "US", label: "United States" },
];

const BusinessAddressForm: React.FC = () => {
  const loadCities = useCallback(async (inputValue: string): Promise<
    SelectOption[]
  > => {
    try {
      const raw = City.getCitiesOfCountry("US");

      if (!raw || raw.length === 0) {
        console.warn("No cities found for US");
        return [];
      }

      const uniqueCities = Array.from(new Set(raw.map(c => c.name)));
      const opts = uniqueCities.map(name => ({ value: name, label: name }));
      const filtered = inputValue
        ? opts.filter(o =>
            o.label.toLowerCase().includes(inputValue.toLowerCase())
          )
        : opts;

      return filtered.slice(0, 50);
    } catch (error) {
      console.error("Error loading cities:", error);
      return [];
    }
  }, []);

  const loadStates = useCallback(async (inputValue: string): Promise<
    SelectOption[]
  > => {
    try {
      const allStates = State.getStatesOfCountry("US");

      if (!allStates || allStates.length === 0) {
        console.warn("No states found for US");
        return [];
      }

      const stateOptions = allStates.map(state => ({
        value: state.isoCode,
        label: state.name,
      }));

      const filtered =
        inputValue && inputValue.length > 0
          ? stateOptions.filter(option =>
              option.label.toLowerCase().includes(inputValue.toLowerCase())
            )
          : stateOptions;

      return filtered.slice(0, 50);
    } catch (error) {
      console.error("Error loading states:", error);
      return [];
    }
  }, []);

  return (
    <WizardFormStep
      title="Add Your Business Address"
      subtitle="Please add your business address or PO Box address"
      className={s.formContainer}
    >
      <FormFieldRow spacing="first">
        <FormInput
          name="addressLine1"
          type="text"
          placeholder="Address"
        />
        <FormInput
          name="addressLine2"
          type="text"
          placeholder="Apartment, Suites, etc."
        />
      </FormFieldRow>
      <FormFieldRow>
        <AsyncSelect
          name="addressCity"
          placeholder="City"
          loadOptions={loadCities}
          defaultOptions={true}
          noOptionsMessage={({ inputValue }) =>
            inputValue ? "No cities found" : "Type to search for a city"
          }
        />
        <AsyncSelect
          name="addressStateProvince"
          placeholder="State/Province"
          loadOptions={loadStates}
          defaultOptions={true}
          noOptionsMessage={({ inputValue }) =>
            inputValue ? "No states found" : "Type to search for a state"
          }
        />
      </FormFieldRow>
      <FormFieldRow>
        <FormInput
          name="addressPostalCode"
          type="text"
          placeholder="Zip Code"
        />
        <BaseSelect
          name="country"
          options={countryOptions}
          placeholder="Country"
          defaultValue={countryOptions[0]}
        />
      </FormFieldRow>
    </WizardFormStep>
  );
};

export default withStyles(s)(BusinessAddressForm);
